/**
 * Whether this principal's next read spans every client company.
 *
 * One definition, consumed by the document list, the statistics and the audit trail. Three copies
 * of the same condition is how the three drift — the lesson `canGroupTogether` already taught in
 * this portal, where a rule copied by hand from the backend refused a lawful group while the
 * server's own answer was unreachable.
 *
 * **True only when the role may reach across *and* no company is chosen.** The two mechanisms are
 * contradictory: `X-Organisation-Id` says *act inside this one company*, `allOrganisations` says
 * *read across all of them*, and `app/api.ts` attaches the header whenever a context exists.
 * Deciding it in one place is what stops a request carrying both — which the server would answer
 * plausibly rather than refuse, since it resolves tenancy before it looks at the query.
 *
 * ⚠️ The ability is half the answer and cannot be dropped. Every ordinary user has a null context
 * too; an empty context alone means only *no choice has been made*, and it takes a role that
 * *could* choose for that to mean *all of them*.
 *
 * The context is taken structurally (`{ id }`) rather than as `OrganisationContext` so that this
 * stays in `deca/`, which is domain and imports nothing from `app/`.
 */

import type { Abilities } from './roles.ts'

export function readsAcrossOrganisations(
  abilities: Abilities,
  context: { id: string } | null | undefined,
): boolean {
  return abilities.choosesOrganisation && !context
}

/**
 * Which company to enter before opening this row, or `null` for "stay where you are".
 *
 * `allOrganisations` is declared on three collection reads and **nothing else**: `GET /decas/{id}`,
 * `/pdf`, `/qr`, `/history` and `/versions` all resolve tenancy from `X-Organisation-Id` alone. So
 * a row found by listing across companies opens onto a 404 unless the context follows it in — which
 * is the journey the design describes, not a workaround for it (§1: "opening it sets the
 * organisation context").
 *
 * Two cases answer `null`, and both matter because entering costs a `queryClient.clear()`:
 *
 *  - the row is **already** in the current company, so a platform admin working inside one company
 *    does not throw the cache away on every row they open;
 *  - the principal cannot choose a company at all — a tenant user's every row is their own company,
 *    and without this check each click would set a context and clear the cache for the four roles
 *    that never do anything cross-company.
 */
export function organisationToEnter(
  abilities: Abilities,
  row: { organisationId: string; organisationName: string },
  context: { id: string } | null | undefined,
): { id: string; name: string } | null {
  if (!abilities.choosesOrganisation) return null
  if (context?.id === row.organisationId) return null
  return { id: row.organisationId, name: row.organisationName }
}
